import type { SnapshotProcess } from '@legalhermes/shared/domain';
import { ChevronDown, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDate } from '../../utils/dates';
import {
  getPartesFromSujetos,
  getProcessStatusKey,
  PROCESS_STATUS,
  sortActuacionesDesc,
} from './cases.helpers';
import type { CaseEntry, Novedad } from './cases.model';

interface ActuacionRowProps {
  fecha: string | null;
  titulo: string | null;
  anotacion: string | null;
  esNueva: boolean;
}

function ActuacionRow({ fecha, titulo, anotacion, esNueva }: ActuacionRowProps) {
  return (
    <div className="py-2.5 border-t border-border first:border-t-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[0.8125rem] text-ink-tertiary tabular-nums">{formatDate(fecha)}</span>
        {esNueva && (
          <span className="bg-accent-subtle text-accent text-[0.6875rem] font-bold px-1.75 py-0 rounded-pill tracking-wide">
            NUEVO
          </span>
        )}
      </div>
      <div className="text-[0.9375rem] font-medium text-ink leading-snug mt-0.5">
        {titulo || 'Sin descripción'}
      </div>
      {anotacion && (
        <div className="text-[0.8125rem] text-ink-tertiary italic leading-snug mt-0.5">
          {anotacion}
        </div>
      )}
    </div>
  );
}

interface ProcessRowProps {
  process: SnapshotProcess;
  expanded: boolean;
  onToggle: () => void;
  isNueva: boolean;
  onNavigate: () => void;
}

function ProcessRow({ process, expanded, onToggle, isNueva, onNavigate }: ProcessRowProps) {
  const statusKey = getProcessStatusKey(process);
  const status = PROCESS_STATUS[statusKey];
  const { demandante, demandado } = getPartesFromSujetos(process.sujetos);

  const campos = [
    {
      label: 'Radicación',
      value: process.fechaRadicacion ? formatDate(process.fechaRadicacion) : null,
    },
    {
      label: 'Última actuación',
      value: process.fechaUltimaActuacion ? formatDate(process.fechaUltimaActuacion) : null,
    },
    { label: 'Demandante', value: demandante },
    { label: 'Demandado', value: demandado },
  ].filter((c) => c.value);

  const sortedActuaciones = sortActuacionesDesc(process.actuaciones || []);
  const shownActuaciones = sortedActuaciones.slice(0, 5);
  const totalActuaciones = process.totalActuaciones ?? sortedActuaciones.length;
  const hasMore = totalActuaciones > shownActuaciones.length;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full text-left flex items-start gap-2.5 px-4 py-3.5 min-h-11 bg-transparent border-none cursor-pointer hover:bg-surface-raised transition-colors duration-150"
      >
        <span className={cn('mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0', status.dotClass)} />
        <div className="flex-1 min-w-0">
          <div className="text-[0.9375rem] font-medium text-ink leading-snug">
            {process.despacho || 'Despacho desconocido'}
          </div>
          <div className="font-mono text-[0.75rem] text-ink-muted tracking-wide truncate mt-0.5">
            {process.numeroRadicacion}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <span
              className={cn(
                'text-[0.78125rem] font-medium px-2 py-px rounded-sm border',
                status.pillClass
              )}
            >
              {status.text}
            </span>
            {process.esPrivado && (
              <span className="inline-flex items-center gap-1 text-[0.78125rem] font-medium px-2 py-px rounded-sm border text-ink-secondary border-border-emphasis bg-surface-raised">
                <Lock size={11} aria-hidden="true" />
                Privado
              </span>
            )}
            {process.fechaUltimaActuacion && (
              <span className="text-[0.8125rem] text-ink-muted">
                Últ. {formatDate(process.fechaUltimaActuacion)}
              </span>
            )}
            {isNueva && (
              <span className="bg-accent-subtle text-accent text-[0.71875rem] font-bold px-1.75 py-0 rounded-pill tracking-wide">
                NUEVO
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          size={14}
          className={cn(
            'flex-shrink-0 text-ink-muted mt-1.5 transition-transform duration-150',
            expanded && 'rotate-180'
          )}
        />
      </button>
      {expanded && (
        <div className="pl-8.25 pr-4 pb-4 pt-0.5">
          <div className="flex flex-col gap-1.75 py-2 pb-3">
            {campos.map(({ label, value }) => (
              <div key={label} className="flex gap-3 items-baseline">
                <span className="text-[0.8125rem] text-ink-muted w-29.5 flex-shrink-0">
                  {label}
                </span>
                <span className="text-[0.90625rem] text-ink">{value}</span>
              </div>
            ))}
          </div>
          <div className="text-[0.75rem] font-semibold text-ink-tertiary tracking-wide uppercase pb-1">
            Actuaciones
          </div>
          <div className="flex flex-col">
            {shownActuaciones.map((act) => (
              <ActuacionRow
                key={act.idRegActuacion}
                fecha={act.fechaActuacion}
                titulo={act.actuacion}
                anotacion={act.anotacion}
                esNueva={false}
              />
            ))}
          </div>
          {hasMore && (
            <div className="text-[0.8125rem] text-ink-muted pt-2">
              +{totalActuaciones - shownActuaciones.length} actuaciones anteriores
            </div>
          )}
          {process.esPrivado ? (
            <div className="flex items-start gap-1.5 mt-3 text-[0.8125rem] text-ink-muted leading-snug">
              <Lock size={12} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>La Rama Judicial no publica el detalle de este proceso porque es privado.</span>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNavigate();
              }}
              className="inline-flex items-center gap-1.5 mt-3 text-[0.8125rem] text-ink-secondary border border-border-emphasis rounded-sm px-3 py-1.5 bg-transparent cursor-pointer hover:text-ink hover:border-border-strong transition-colors duration-150"
            >
              Ver expediente completo
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface CaseCardProps {
  entry: CaseEntry;
  novedad: Novedad | undefined;
  expanded: boolean;
  onToggle: () => void;
  expandedProcKey: string | null;
  onToggleProc: (key: string) => void;
  onNavigateProcess: (idProceso: number) => void;
}

export function CaseCard({
  entry,
  novedad,
  expanded,
  onToggle,
  expandedProcKey,
  onToggleProc,
  onNavigateProcess,
}: CaseCardProps) {
  const n = entry.processes.length;
  const meta = `${n} ${n === 1 ? 'proceso' : 'procesos'} · ${
    entry.ultimoMovimiento
      ? `Último movimiento: ${formatDate(entry.ultimoMovimiento)}`
      : 'Sin movimientos registrados'
  }`;

  return (
    <div
      className={cn(
        'bg-surface border rounded-lg overflow-hidden',
        novedad ? 'border-accent/35' : 'border-border'
      )}
    >
      <button
        onClick={onToggle}
        className="w-full text-left flex items-start gap-3 px-4 py-4 min-h-11 bg-transparent border-none cursor-pointer hover:bg-surface-raised transition-colors duration-150"
      >
        <span
          className={cn(
            'mt-2.25 w-2 h-2 rounded-full flex-shrink-0',
            novedad ? 'bg-accent' : 'bg-transparent'
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2.5 flex-wrap">
            <span
              className={cn(
                'text-[1.0625rem] font-semibold text-ink leading-snug',
                entry.searchType === 'process' ? 'font-mono tracking-wide' : 'font-serif'
              )}
            >
              {entry.nombre}
            </span>
            {novedad && (
              <span className="bg-accent-subtle border border-accent/30 text-accent text-[0.78125rem] font-semibold px-2.25 py-px rounded-pill whitespace-nowrap">
                {novedad.texto}
              </span>
            )}
          </div>
          <div className="text-[0.875rem] text-ink-tertiary mt-0.5">{meta}</div>
        </div>
        <ChevronDown
          size={16}
          className={cn(
            'flex-shrink-0 text-ink-tertiary mt-2 transition-transform duration-150',
            expanded && 'rotate-180'
          )}
        />
      </button>
      {expanded && (
        <div className="border-t border-border bg-surface-inset">
          {entry.processes.map((process, idx) => {
            const procKey = `${entry.key}:${idx}`;
            return (
              <ProcessRow
                key={process.idProceso ?? idx}
                process={process}
                expanded={expandedProcKey === procKey}
                onToggle={() => onToggleProc(procKey)}
                isNueva={idx === 0 && !!novedad}
                onNavigate={() => onNavigateProcess(process.idProceso)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
